export interface AccessMatrixItem {
  module: string;
  subModule: string;
  action: string;
  accessLevel: string;
  admin: 'FULL' | 'DENIED' | 'PUBLIC';
  editor: 'FULL' | 'DENIED' | 'PUBLIC';
  sales: 'FULL' | 'DENIED' | 'PUBLIC';
  warehouse: 'FULL' | 'DENIED' | 'PUBLIC';
  description: string;
}

export const USER_ACCESS_MATRIX: AccessMatrixItem[] = [
  {
    module: "User Management",
    subModule: "Admin Settings",
    action: "Access Admin Settings View",
    accessLevel: "UI View: /admin-settings",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Restricted view for workspace administration and role management."
  },
  {
    module: "User Management",
    subModule: "User Accounts",
    action: "List All Provisioned CRM Users",
    accessLevel: "GET /api/users",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Backend enforced by requireAdmin middleware."
  },
  {
    module: "User Management",
    subModule: "User Accounts",
    action: "Add New User & Send Invite",
    accessLevel: "POST /api/users",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Adds user to database and sends Gmail invitation email (@armellini.com required)."
  },
  {
    module: "User Management",
    subModule: "User Roles",
    action: "Change User Role Tier",
    accessLevel: "PATCH /api/users/:uid/role",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Allows promoting/demoting user roles (admin prevents self-demotion)."
  },
  {
    module: "User Management",
    subModule: "User Accounts",
    action: "Delete User Account",
    accessLevel: "DELETE /api/users/:uid",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Removes user access permanently (admin prevents self-deletion)."
  },
  {
    module: "System Configuration",
    subModule: "Taxonomies",
    action: "Add New Industry Option",
    accessLevel: "POST /api/industries",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Backend enforced by requireAdmin middleware."
  },
  {
    module: "System Configuration",
    subModule: "Taxonomies",
    action: "Delete Industry Option",
    accessLevel: "DELETE /api/industries/:name",
    admin: "FULL",
    editor: "DENIED",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Backend enforced by requireAdmin middleware."
  },
  {
    module: "System Configuration",
    subModule: "System Maintenance",
    action: "Access System Maintenance View",
    accessLevel: "UI View: /settings",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Configures system-wide defaults and operational parameters."
  },
  {
    module: "CRM Leads",
    subModule: "Dashboard",
    action: "Access CRM Dashboard View",
    accessLevel: "UI View: /dashboard",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Pipeline, Table, and Kanban views of all active sales leads."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Records",
    action: "View & Query Sales Leads",
    accessLevel: "GET /api/leads",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Fetches active and archived sales leads."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Records",
    action: "Create New Lead Record",
    accessLevel: "POST /api/leads",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Creates a new lead entry in the CRM pipeline."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Records",
    action: "Edit & Update Lead Details",
    accessLevel: "POST /api/leads",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Updates contact, address, quoting details, and milestone dates."
  },
  {
    module: "CRM Leads",
    subModule: "Automation",
    action: "AI Lead Status Recommendation",
    accessLevel: "POST /api/leads/recommend-status",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Analyzes next steps/log notes to suggest updated lead status."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Operations",
    action: "Duplicate Existing Lead",
    accessLevel: "POST /api/leads/duplicate",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Clones lead details with activity log tracking."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Operations",
    action: "Archive Sales Lead",
    accessLevel: "POST /api/leads/archive",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Soft-archives a lead from active views."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Operations",
    action: "Restore Archived Lead",
    accessLevel: "POST /api/leads/restore",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Restores soft-archived lead back to active pipeline."
  },
  {
    module: "CRM Leads",
    subModule: "Lead Operations",
    action: "Hard Delete Lead Record",
    accessLevel: "DELETE /api/leads/:id",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes lead record. Enforced by requireEditor middleware."
  },
  {
    module: "CRM Leads",
    subModule: "Automation",
    action: "Run 30-Day Expired Lead Archival",
    accessLevel: "POST /api/leads/archive-expired",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Auto-archives leads older than 30 days. Enforced by requireEditor."
  },
  {
    module: "CRM Leads",
    subModule: "Public Ingestion",
    action: "Public Lead Capture Submission",
    accessLevel: "POST /api/public/lead-capture",
    admin: "PUBLIC",
    editor: "PUBLIC",
    sales: "PUBLIC",
    warehouse: "PUBLIC",
    description: "Unauthenticated public endpoint protected by honeypot & IP rate-limiting."
  },
  {
    module: "CRM Contacts",
    subModule: "Contacts Directory",
    action: "Access CRM Contacts View",
    accessLevel: "UI View: /contacts",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Customer master view for managing accounts and client histories."
  },
  {
    module: "CRM Contacts",
    subModule: "Service Quotes",
    action: "View Quotes for Account",
    accessLevel: "GET /api/quotes/:accountId",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Lists service quoting history for a specific customer account."
  },
  {
    module: "CRM Contacts",
    subModule: "Service Quotes",
    action: "Create Service Quote",
    accessLevel: "POST /api/quotes",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Generates a new rate/service quote for an account."
  },
  {
    module: "CRM Contacts",
    subModule: "Service Quotes",
    action: "Hard Delete Service Quote",
    accessLevel: "DELETE /api/quotes/:id",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently removes service quote. Enforced by requireEditor."
  },
  {
    module: "Directory Lookup",
    subModule: "Consignee & Shipper",
    action: "Access Directory Lookup View",
    accessLevel: "UI View: /cdata-search",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Search interface for consignee, shipper, contact, and ETA data."
  },
  {
    module: "Directory Lookup",
    subModule: "Master Search",
    action: "Query CData & AELIS Proxy Data",
    accessLevel: "GET /api/cdata-proxy",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Reads live/mock CData views (consignees, shippers, contacts, days of service)."
  },
  {
    module: "Non-Floral LTL",
    subModule: "LTL Workspace",
    action: "Access Non-Floral LTL Module",
    accessLevel: "UI View: /non-floral",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Main hub for Non-Floral LTL freight management."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Sub-Modules",
    action: "Data Entry / Active LTL Sub-tab",
    accessLevel: "UI Sub-Tab: active",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages pending signature and active LTL shipments."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Sub-Modules",
    action: "Billing History Sub-tab",
    accessLevel: "UI Sub-Tab: history",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Historical view of billed and closed LTL freight."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Sub-Modules",
    action: "Lara Email Queue Sub-tab",
    accessLevel: "UI Sub-Tab: lara",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Email queue management for automated dispatch and notification."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Sub-Modules",
    action: "Account Maintenance Sub-tab",
    accessLevel: "UI Sub-Tab: maintenance",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Master record management for shippers, consignees, bill-tos, commodities."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Sub-Modules",
    action: "Audit Log Sub-tab",
    accessLevel: "UI Sub-Tab: audit",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "System audit log of non-floral operations."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Shipments",
    action: "View Non-LTL Shipments",
    accessLevel: "GET /api/nonltl-shipments",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Retrieves all active and historical non-LTL shipments."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Shipments",
    action: "Create / Update Non-LTL Shipment",
    accessLevel: "POST /api/nonltl-shipments",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Creates or updates non-LTL shipment quote details."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Shipments",
    action: "Hard Delete Non-LTL Shipment",
    accessLevel: "DELETE /api/nonltl-shipments/:quoteNumber",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes shipment. Enforced by requireEditor middleware."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Integrations",
    action: "Google Sheet Mass Sync",
    accessLevel: "POST /api/nonltl-shipments/sheet-sync",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Bulk upserts shipment rows directly from connected Google Sheet."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Shippers",
    action: "View & Upsert Shippers",
    accessLevel: "GET/POST /api/shippers",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages grower/shipper master details."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Shippers",
    action: "Delete Shipper Record",
    accessLevel: "DELETE /api/shippers/:name",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes shipper record. Enforced by requireEditor."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Consignees",
    action: "View & Upsert Consignees",
    accessLevel: "GET/POST /api/consignees",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages receiver/consignee master details."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Consignees",
    action: "Delete Consignee Record",
    accessLevel: "DELETE /api/consignees/:account",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes consignee record. Enforced by requireEditor."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Bill-To Accounts",
    action: "View & Upsert Bill-To Accounts",
    accessLevel: "GET/POST /api/billtos",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages billing party master details."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Bill-To Accounts",
    action: "Delete Bill-To Account",
    accessLevel: "DELETE /api/billtos/:account",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes bill-to record. Enforced by requireEditor."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Commodities",
    action: "View & Upsert Commodities",
    accessLevel: "GET/POST /api/commodities",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages freight commodity definitions."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Commodities",
    action: "Delete Commodity Definition",
    accessLevel: "DELETE /api/commodities/:commodity",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes commodity. Enforced by requireEditor."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Lara Queue",
    action: "View & Upsert Lara Emails",
    accessLevel: "GET/POST /api/lara-emails",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Manages pending dispatch notifications."
  },
  {
    module: "Non-Floral LTL",
    subModule: "Lara Queue",
    action: "Delete Lara Email Entry",
    accessLevel: "DELETE /api/lara-emails/:id",
    admin: "FULL",
    editor: "FULL",
    sales: "DENIED",
    warehouse: "DENIED",
    description: "Permanently deletes email queue item. Enforced by requireEditor."
  },
  {
    module: "AELIS 4 Logistics",
    subModule: "AELIS Integration",
    action: "Access AELIS Logistics Hub",
    accessLevel: "UI View: /aelis-logistics",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "AELIS 4 logistics operations hub."
  },
  {
    module: "AELIS 4 Logistics",
    subModule: "AELIS API",
    action: "Query AELIS Doors / Warehouse / Inventory",
    accessLevel: "GET /api/aelis/*",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Fetches live warehouse doors, layouts, cutoffs, cooler inventory, receipts."
  },
  {
    module: "AELIS 4 Logistics",
    subModule: "AELIS Operations",
    action: "Process Dock Receiving & PutAway",
    accessLevel: "POST /api/aelis/receiving/*",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Executes unit dock receiving and cooler putaway operations."
  },
  {
    module: "AELIS 4 Logistics",
    subModule: "AELIS Operations",
    action: "Upload Shipment Orders XML",
    accessLevel: "POST /api/aelis/data-entry/shipment/upload",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Uploads AELIS shipment order XML payloads."
  },
  {
    module: "Analytics & Audit",
    subModule: "Analytics",
    action: "Access Analytics & Reports View",
    accessLevel: "UI View: /analytics",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Displays revenue metrics, sales KPIs, pipeline conversion, and ROI."
  },
  {
    module: "Analytics & Audit",
    subModule: "Audit Log",
    action: "View System Audit Log",
    accessLevel: "GET /api/audit-log",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Fetches audit trail entries for data additions, modifications, and deletions."
  },
  {
    module: "Integrations",
    subModule: "Google Workspace",
    action: "Access Google Hub View",
    accessLevel: "UI View: /google-hub",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Interface for Gmail thread tracking and Google Drive file attachments."
  },
  {
    module: "Integrations",
    subModule: "Gmail Tracking",
    action: "Track Outbound Emails",
    accessLevel: "GET/POST /api/tracked-emails",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Tracks sent emails and reply status."
  },
  {
    module: "Integrations",
    subModule: "Client Forms",
    action: "Access Client Forms Hub",
    accessLevel: "UI View: /client-forms",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "DENIED",
    description: "Dispatches rate agreements and credit application forms."
  },
  {
    module: "AI Copilot",
    subModule: "AI Assistant",
    action: "Interactive Logistics AI Copilot",
    accessLevel: "POST /api/ai/chat",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "AI assistant powered by Gemini / custom LLM with Dgraph memory."
  },
  {
    module: "User Profile",
    subModule: "Personalization",
    action: "Access Profile View",
    accessLevel: "UI View: /profile",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "View and manage user profile and email signature."
  },
  {
    module: "User Profile",
    subModule: "Personalization",
    action: "Update Profile & Signature",
    accessLevel: "GET/POST /api/profile",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Updates user display name and email signature."
  },
  {
    module: "User Profile",
    subModule: "Personalization",
    action: "Manage Dashboard Preferences",
    accessLevel: "GET/POST /api/user-preferences",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "Per-user preferences for dashboard columns, filters, sorting, smart views."
  },
  {
    module: "Help & Support",
    subModule: "Help Center",
    action: "Access Help Center View",
    accessLevel: "UI View: /help-center",
    admin: "FULL",
    editor: "FULL",
    sales: "FULL",
    warehouse: "FULL",
    description: "User documentation and system guides."
  }
];
